import { isEmpty } from "lodash";

const SHOP_API = `${ENV.SHOPS_BASE_URL}/api/v1`;

const abstractModel = (_resource) => {
  let resource = _resource;

  const processRequest = async (path, options) => {
    try {
      const response = await fetch(`${SHOP_API}/${path}`, options);

      if (!response.ok) {
        const errorDetails = await response.json().catch(() => ({}));
        throw {
          type: 'HTTPError',
          status: response.status,
          message: { message: errorDetails.message || errorDetails.errors || `HTTP error! Status: ${response.status}` },
          url: response.url
        };
      }

      const json = await response.json().catch((parseError) => {
        throw {
          type: 'ParseError',
          message: 'Error parsing JSON response',
          details: parseError.message || parseError.errors
        };
      });

      checkResponse(response, json);
      return json;

    } catch (error) {
      handleFetchError(error);
    }
  };

  const handleFetchError = (error) => {
    if (error.type === 'HTTPError') {
      console.error(`HTTP Error: ${error.message} (Status: ${error.status}) - URL: ${error.url}`);
    } else if (error.type === 'ParseError') {
      console.error(`Parse Error: ${error.message}. Details: ${error.details}`);
    } else {
      console.error(`Network or unknown error: ${error.message || error}`);
    }
    throw error.message || "An unknown error occurred";
  };

  const get = async (params = {}, newResource = {}) => {
    let newPath = newResource.path || resource;
    const filters = newResource.filters || null;

    if (filters) newPath = `${newPath}?${filters}`;

    if (!isEmpty(params)) {
      const decorate = filters ? '&' : '?';

      newPath = `${newPath}${decorate}${new URLSearchParams(params).toString()}`;
    };

    const options = {
      method: 'GET',
      headers: setHeader()
    };

    return processRequest(newPath, options);
  };

  const post = async (params = {}, newResource = {}) => {
    const multipart = params instanceof FormData;

    const options = {
      method: 'POST',
      headers: setHeader(multipart),
      body: multipart ? params : JSON.stringify(params)
    };

    return processRequest(newResource.path || resource, options);
  };

  const save = async (params = {}, newResource = {}) => {
    let method = 'POST';
    let newPath = newResource.path || resource;

    const multipart = params instanceof FormData;

    if (multipart && params.get('id') && params.get('id') != 'null') {
      method = 'PUT';
      newPath = `${newPath}/${params.get('id')}`
      params.delete('id');
    }
    else if (params.id) {
      method = 'PUT';
      newPath = `${newPath}/${params.id}`
      delete params.id;
    }

    const options = {
      method,
      headers: setHeader(multipart),
      body: multipart ? params : JSON.stringify(params)
    };

    return processRequest(newPath, options);
  };

  const remove = async (params = {}, newResource = {}) => {
    let newPath = newResource.path || resource;

    if (params.id) newPath = `${newPath}/${params.id}`;

    const options = {
      method: 'DELETE',
      headers: setHeader(),
      body: JSON.stringify(params)
    };

    return processRequest(newPath, options);
  };

  let resultModel = {
    get,
    findById: async (id, params = {}) => {
      try {
        const path = `${resource}/${id}`;
        const elements = await get.call(this, params, { path });
        return _.isEmpty(elements) ? null : elements;
      }
      catch (err) {
        throw err;
      }
    },
    post,
    save,
    remove
  };

  return resultModel;
}

export default abstractModel;

function setHeader(multipart = false) {
  let headers = {
    'MERCATELY-AUTH-TOKEN': ENV.MERCATELY_AUTH_TOKEN,
    'RETAILER-KEY': ENV.UNIQUE_KEY
  }

  if (!multipart) {
    headers['Accept'] = 'application/json, text/plain';
    headers['Content-Type'] = 'application/json';
  }

  return headers;
}

function checkResponse(response, json) {
  if (response.status !== 200) {
    throw {
      errors: json.message || json.errors,
      status: response.status
    };
  }
}
